import { useMemo, useRef } from "react";
import { Info } from "lucide-react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";

import styles from "./custom-input-select.module.scss";

const mapValueToOption = (val, options) => {
	if (val === undefined || val === null || val === "") return null;
	const opt = options?.find((o) => o?.value === val);
	return opt || { value: val, label: String(val) };
};
const mapValueToOptions = (vals, options) => (Array.isArray(vals) ? vals.map((v) => mapValueToOption(v, options)).filter(Boolean) : []);

const mapOptionsToValue = (opt, isMulti) => (isMulti ? (opt || []).map((o) => o.value) : opt ? opt.value : null);

const getSelectStyles = (size = "md", hasError = false, minimal = false) => {
	const paddings = { sm: "6px 10px", md: "8px 6px", lg: "10px 12px" };
	const fontSizes = { sm: 14, md: 15, lg: 16 };

	const options = {
		control: {
			height: "auto",
			minHeight: "30px",
			maxHeight: "70px",
		},
		valueContainer: {
			minHeight: "30px",
			height: "auto",
			maxHeight: "70px",
			overflow: "auto",
		},
		indicatorsContainer: {
			overflow: "auto",
			paddingRight: 0,
		},
	};

	return {
		control: (base, state) => ({
			...base,
			minHeight: "unset",
			borderRadius: 5,
			borderColor: hasError ? "#ef4444" : state.isFocused ? "#c7d2fe" : "#e5e7eb",
			boxShadow: state.isFocused ? (hasError ? "0 0 0 3px rgba(239,68,68,0.15)" : "0 0 0 3px rgba(59,130,246,0.15)") : "none",
			padding: 0,
			"&:hover": { borderColor: hasError ? "#ef4444" : "#cbd5e1" },
			fontSize: fontSizes[size],
			...(minimal ? options.control : []),
		}),
		valueContainer: (base) => ({ ...base, padding: paddings[size], ...(minimal ? options.valueContainer : []) }),
		input: (base) => ({ ...base, margin: 0, padding: 0 }),
		indicatorsContainer: (base) => ({ ...base, paddingRight: 8, ...(minimal ? options.indicatorsContainer : []) }),
		clearIndicator: (base) => ({ ...base, padding: 6 }),
		dropdownIndicator: (base) => ({ ...base, padding: 6 }),
		multiValue: (base) => ({ ...base, borderRadius: 5 }),
		option: (base, state) => ({
			...base,
			fontSize: fontSizes[size],
			backgroundColor: state.isSelected ? "#e5f4d6" : state.isFocused ? "#f3f4f6" : "transparent",
			color: "#111827",
		}),
		menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden" }),
	};
};

const CustomInputSelect = ({
	id,
	name,
	label,
	options = [],
	value,
	onChange,
	placeholder = "Seleccionar...",
	required = false,
	disabled = false,
	error = "",
	helper = "",
	size = "md",
	col = 12,
	isMulti = false,

	async = false,
	minChars = 3,
	loadOptions,
	defaultOptions = false,

	creatable = false,
	onCreateOption,
	formatCreateLabel = (input) => `Crear "${input}"`,

	isClearable = true,

	components = {},

	className,
	style,
	autoFocus = false,
	formatOptionLabel,

	minimal = false,
}) => {
	const stableIdRef = useRef(id || `${name || "select"}-${Math.random().toString(36).slice(2)}`);
	const inputId = stableIdRef.current;

	const colClass = col === 3 ? styles.col3 : col === 6 ? styles.col6 : styles.col12;
	const describedBy = error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined;

	const selected = useMemo(() => {
		if (isMulti) {
			return mapValueToOptions(value, options);
		}
		return mapValueToOption(value, options);
	}, [value, options, isMulti]);

	const handleChange = (opt) => onChange?.(mapOptionsToValue(opt, isMulti));

	const defaultLoadOptions = async (inputValue) => {
		const q = (inputValue || "").trim().toLowerCase();
		if (q.length < minChars) return [];
		return (options || []).filter((o) => String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q));
	};

	const SelectImpl = useMemo(() => {
		if (async && creatable) return AsyncCreatableSelect;
		if (async) return AsyncSelect;
		if (creatable) return CreatableSelect;
		return Select;
	}, [async, creatable]);

	const selectBaseStyles = getSelectStyles(size, !!error, minimal);
	const selectStyles = {
		...selectBaseStyles,
		menuPortal: (base) => ({ ...base, zIndex: 9999 }),
	};

	const asyncProps = async
		? {
				cacheOptions: true,
				defaultOptions,
				loadOptions: loadOptions || defaultLoadOptions,
				filterOption: null,
			}
		: {};

	const creatableProps = creatable ? { onCreateOption, formatCreateLabel } : {};

	const descriptions = useMemo(() => {
		if (isMulti) {
			return (Array.isArray(selected) ? selected : []).map((o) => o?.description).filter(Boolean);
		}
		return selected?.description ? [selected.description] : [];
	}, [selected, isMulti]);

	return (
		<div className={`${styles.field} ${colClass} ${className || ""}`} style={style}>
			{label ? (
				<label htmlFor={inputId} className={styles.label}>
					{label} {required ? <span className={styles.req}>*</span> : null}
				</label>
			) : null}
			<SelectImpl
				inputId={inputId}
				name={name}
				isMulti={isMulti}
				isDisabled={disabled}
				isClearable={isClearable}
				placeholder={placeholder}
				value={selected}
				onChange={handleChange}
				classNamePrefix="ci-select"
				noOptionsMessage={({ inputValue }) =>
					async && (inputValue || "").length < minChars ? `Escribe al menos ${minChars} caracteres` : "Sin opciones"
				}
				options={!async ? options : undefined}
				autoFocus={autoFocus}
				aria-invalid={!!error || undefined}
				aria-describedby={describedBy}
				menuPortalTarget={typeof document !== "undefined" ? document.body : null}
				menuPosition="fixed"
				styles={selectStyles}
				menuPlacement="auto"
				menuShouldScrollIntoView={false}
				components={components}
				formatOptionLabel={formatOptionLabel}
				{...asyncProps}
				{...creatableProps}
			/>{" "}
			{descriptions.length > 0 && (
				<div className={styles.desc}>
					{descriptions.map((d, idx) => (
						<div key={idx} className={styles.descItem}>
							<Info className={styles.descIcon} size={16} />
							<span>{d}</span>
						</div>
					))}
				</div>
			)}
			{error ? (
				<div id={`${inputId}-error`} className={styles.error}>
					{error}
				</div>
			) : helper ? (
				<div id={`${inputId}-helper`} className={styles.helper}>
					{helper}
				</div>
			) : null}
		</div>
	);
};

export default CustomInputSelect;
